/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';

import { Period }          from '@/util/period';

import * as $ from 'jquery';

import ClickEvent    = JQuery.ClickEvent;
import KeyDownEvent  = JQuery.KeyDownEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class PeriodEditor
 *
 * @brief mini-form for editing a "period".
 *
 * Basically the format is:
 *
 *   P[n]Y[n]M[n]DT[n]H[n]M[n]S
 *
 * we really only need to support P[n]DT[n]H[n]M[n]S; train simulations only run for weeks not months or years.
 *
 * @link https://en.wikipedia.org/wiki/ISO_8601
 * @link https://www.digi.com/resources/documentation/digidocs/90001437-13/reference/r_iso_8601_duration_format.htm
 * @link ftp://ftp.ripe.net/test-traffic/ROOT/delayplots/iso8601.C
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class PeriodEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement;

  /** the callback to tell agGrid we're done editing */
  protected editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  protected clickListenter: (e: MouseEvent) => void = null;

  protected tabBlocker: (e: KeyboardEvent) => void = null;

  /** the current value (if there is one */
  protected value: Period = null;

  /** some subclasses may want only the time (limit of 24 hours max) and not days */
  protected noDays: boolean = false;

  /** helper to fetch a legit value from a number input */
  public parseNumber(el: JQuery, min: number = 0, max: number  = 59) {

    if ($(el).val() === '') {

      $(el).val(min);

      return min;
    }

    let value: number  = parseInt($(el).val() as string, 10);

    if (isNaN(value)) {

      value = 0;
    }

    if (value < 0) {

      value *= -1;
    }

    if (value < min) {

      value = min;
    }

    if (value > max) {

      value = max;
    }

    /* reset the current value in the field */

    $(el).val(value);

    return value;
  }

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    this.editingDone = params.stopEditing;
    this.value       = null;

    const ce         = this;

    /* use existing cell value if there is one */

    if (params.value && (params.value instanceof Period)) {

      this.value = new Period();

      Object.assign( this.value, params.value);

    } else {

      this.value = new Period();

    }


    /* opened with Delete? */

    if ((params.eventKey ?? '').length === 1) {

      if (params.eventKey.charCodeAt(0) === 46) {

        /* start with a blank value */

        this.value = new Period();
      }
    }

    if ((params.eventKey ?? '').length === 1) {

      if (this.noDays) {

        this.value._hours = parseInt(params.eventKey);

        if (isNaN(this.value._hours)) {

          this.value._hours = 0;
        }

      } else {

        this.value._days = parseInt(params.eventKey);

        if (isNaN(this.value._days)) {

          this.value._days = 0;
        }
      }
    }

    /* set up the mini-form... */

    this.gui          = document.createElement('div');

    /* insert new HTML for our custom mini-form ... */

    const d = this.value._days;
    const h = this.value._hours;
    const s = this.value._secs;
    const m = this.value._minutes;

    let dayForm = `
<input tabindex="1" class="work-period-day-input"    type="text" value="${d}">
<span class="work-period-separator">:</span>`;

    if (this.noDays) {

      dayForm = '';
    }

    const html = `
<div class="accept-button" style="margin-left: 2px;">
  <i class="fas fa-check"></i>
</div>
<div class="work-period-form">
${dayForm}
<input  tabindex=2 class="work-period-hour-input"   type="text" value="${h}">
<span class="work-period-separator">:</span>
<input  tabindex="3" class="work-period-minute-input" type="text"  value="${m}">
<span class="work-period-separator">:</span>
<input  tabindex="4" class="work-period-second-input" type="text" value="${s}">
</div>`;

    $(this.gui).html(html);

    /* add "accept" behavior; clicking the accept button */

    $(this.gui).find('.accept-button').on('click', (e: ClickEvent) => {

      e.preventDefault();

      /* tell agGrid we're done editing */

      this.editingDone(true);
    });

    const    dayEl: JQuery = $(this.gui).find('.work-period-day-input');
    const   hourEl: JQuery = $(this.gui).find('.work-period-hour-input');
    const minuteEl: JQuery = $(this.gui).find('.work-period-minute-input');
    const secondEl: JQuery = $(this.gui).find('.work-period-second-input');

    /* day must be 0..99 only */

    $(dayEl).on('input', () => {

      this.value._days = this.parseNumber(dayEl, 0, 999);

    });

    /* hour must be 0..59 only */

    $(hourEl).on('input', () => {

      this.value._hours = this.parseNumber(hourEl, 0, 24);

    });

    /* minute must be 0..59 only */

    $(minuteEl).on('input', () => {

      this.value._minutes = this.parseNumber(minuteEl, 0, 59);

    });

    /* second must be 0..59 only */

    $(secondEl).on('input', () => {

      this.value._secs = this.parseNumber(secondEl, 0, 59);

    });

    /* start with focus on day/hour */

    if (this.noDays) {

      $(hourEl).trigger('focus');

      $(hourEl).val('');
      $(hourEl).val(h);

    } else {

      $(dayEl).trigger('focus');

      $(dayEl).val('');
      $(dayEl).val(d);
    }

    /* when they Tab or Shift+Tab, we want to move through the fields */

    $(this.gui).on('keydown', (e: KeyDownEvent) => {

      if ((e.key === 'Escape') || (e.key === 'Enter')) {

        e.preventDefault();
        e.stopPropagation();

        this.editingDone(true);
      }

    });

    $(this.gui).on('keydown', (e: KeyDownEvent) => {

      if (e.key !== 'Tab') {

        /* we only handle the Tab key */

        return ;
      }

      const  focus: JQuery = $(this.gui).find(`:focus`);
      const inputs: JQuery = $(this.gui).find(`.work-period-form`).children('input');

      if ( ! $(focus).is(`input`) ) {

        return ;
      }

      /* figure out the current field position */

      const index: string = $(focus).prop(`tabindex`);
      let     pos: number = parseInt(index, 10);

      e.preventDefault();

      if (isNaN(pos)) {

        /* ? */

        return ;
      }

      if (this.noDays) {

        /* if we aren't showing the days, tabindex 2 is tabindex 1 etc. */

        pos--;
      }

      if ( !e.shiftKey &&  (e.key === 'Tab')) {

        /* moving to the right */

        pos++;

        if (this.noDays) {

          if (pos > 3) {

            pos = 1;
          }

        } else {

          if (pos > 4) {

            pos = 1;
          }
        }

      } else if (e.shiftKey && (e.key === 'Tab')) {

        /* moving to the left */

        pos--;

        if (this.noDays) {

          if (pos < 1) {

            pos = 3;
          }

        } else {

          if (pos < 1) {

            pos = 4;
          }
        }

      } else {

        /* not a combo we watch for */

        return ;
      }

      /* select N'th one to focus on (zero based indexing) */

      const next: JQuery = $(inputs).eq(pos - 1);
      let  value: any    = parseInt($(next).val() as string);

      if (isNaN(value)) {

        value = '';
      }

      if (value === 0) {

        value = '';
      }

      $(next).trigger('focus');
      $(next).val('');
      $(next).val(value)

      /* all done */

      return false;
    });

    /*
     * outside our editor, block the Tab key while the editor is up, so they can't
     * mistakenly tab to the address bar or other browser controls and reload the page.
     *
     */

    this.tabBlocker = (e: KeyboardEvent) => {

      if (e.key === 'Tab') {

        e.preventDefault();
        return false;
      }

    };

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    this.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if (target === this.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', ce.clickListenter);

      this.editingDone(true);
    };

    document.addEventListener('click', ce.clickListenter);

    document.addEventListener('keypress', this.tabBlocker);

  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

    const    dayEl: JQuery = $(this.gui).find('.work-period-day-input');
    const   hourEl: JQuery = $(this.gui).find('.work-period-hour-input');

    /* start with focus on day/hour */

    if (this.noDays) {

      $(hourEl).trigger('focus');

    } else {

      $(dayEl).trigger('focus');

    }
  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): Period {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    document.removeEventListener('keypress', this.tabBlocker);

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined  {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    return false;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
